import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import Modal from "react-bootstrap/Modal";

import AuthService from "../../utilities/services/auth.service";

import DepositModalContent from "./DepositModalContent";

const DepositContext = createContext(undefined);

export const useDepositModal = () => {
  const ctx = useContext(DepositContext);
  if (!ctx) {
    throw new Error("useDepositModal must be used within DepositProvider");
  }
  return ctx;
};

export function DepositProvider({ children }) {
  const organizations = useMemo(() => AuthService.getOrg() || [], []);
  const paymentOrg = organizations?.[0];

  const isProd = process.env.REACT_APP_STAGE === "production";

  const connectedAccountId = isProd
    ? paymentOrg?.connected_account_id
    : paymentOrg?.test_connected_account_id;

  const isDemoOrPreview =
    process.env.REACT_APP_STAGE === "demo" ||
    process.env.REACT_APP_STAGE === "preview";

  const [stripeAccountContext, setStripeAccountContext] = useState(() =>
    paymentOrg?.destinationAccount === false
      ? connectedAccountId
      : isDemoOrPreview || paymentOrg?.destinationAccount === true
        ? null
        : connectedAccountId,
  );

  const stripePromise = useMemo(
    () =>
      loadStripe(
        process.env.REACT_APP_STRIPE_KEY,
        stripeAccountContext
          ? { stripeAccount: stripeAccountContext }
          : undefined,
      ),
    [stripeAccountContext],
  );

  const [showDeposit, setShowDeposit] = useState(false);
  const [depositsRefreshTrigger, setDepositsRefreshTrigger] = useState(0);

  const bumpDepositsRecorded = useCallback(() => {
    setDepositsRefreshTrigger((n) => n + 1);
  }, []);

  const handleDepositShow = useCallback(() => setShowDeposit(true), []);
  const handleDepositClose = useCallback(() => {
    setShowDeposit(false);
    setStripeAccountContext(
      paymentOrg?.destinationAccount === false
        ? connectedAccountId
        : isDemoOrPreview || paymentOrg?.destinationAccount === true
          ? null
          : connectedAccountId,
    );
  }, [connectedAccountId, isDemoOrPreview, paymentOrg?.destinationAccount]);

  const value = useMemo(
    () => ({
      handleDepositShow,
      depositsRefreshTrigger,
      bumpDepositsRecorded,
    }),
    [handleDepositShow, depositsRefreshTrigger, bumpDepositsRecorded],
  );

  return (
    <DepositContext.Provider value={value}>
      <Modal
        centered
        animation={false}
        fullscreen="md-down"
        show={showDeposit}
        onHide={handleDepositClose}
        backdrop="static"
        className="modal-xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>New deposit</Modal.Title>
        </Modal.Header>
        <Elements stripe={stripePromise}>
          <DepositModalContent
            show={showDeposit}
            closeDeposit={handleDepositClose}
            connectedAccountId={connectedAccountId}
            stripeAccountContext={stripeAccountContext}
            onStripeAccountContextChange={setStripeAccountContext}
            onDepositRecorded={bumpDepositsRecorded}
          />
        </Elements>
      </Modal>
      {children}
    </DepositContext.Provider>
  );
}
